/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Build or edit a report.
 *
 * Three panes: pick fields on the left, see results in the middle, shape them
 * on the right. The preview re-runs on a debounce as the definition changes,
 * because the alternative — a Run button — means every mistake costs a
 * deliberate action to discover, and most report building is trial and error.
 *
 * The object and field lists come from /analytics/catalog, which is the same
 * allowlist the engine validates against. The builder therefore cannot offer a
 * field the engine would reject, and a 422 from the preview means a real bug
 * rather than a user mistake.
 *
 * Replaces layouts/crm/CustomReport.js, whose object list was ten hardcoded
 * strings and whose output was never saved.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Chip, CircularProgress, Divider, IconButton,
  InputAdornment, LinearProgress, MenuItem, Snackbar, Tab, Tabs, TextField,
  Tooltip, Typography,
} from '@mui/material';
import {
  AddRounded, ArrowBackRounded, ClearRounded, DragIndicatorRounded,
  FunctionsRounded, SearchRounded, TuneRounded,
} from '@mui/icons-material';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import { useAuth } from 'auth/Auth';
import colors from 'styles/tokens/colors';
import ReportTable from './ReportTable';

/** Ranges the date filter offers, in the order people reach for them. */
const RELATIVE_RANGES = [
  'TODAY', 'YESTERDAY', 'THIS_WEEK', 'LAST_WEEK', 'THIS_MONTH', 'LAST_MONTH',
  'MONTH_TO_DATE', 'THIS_QUARTER', 'LAST_QUARTER', 'QUARTER_TO_DATE',
  'THIS_YEAR', 'LAST_YEAR', 'YEAR_TO_DATE',
  'LAST_N_DAYS:7', 'LAST_N_DAYS:30', 'LAST_N_DAYS:90',
  'NEXT_N_DAYS:7', 'NEXT_N_DAYS:30',
];

const rangeLabel = (token) =>
  token.replace(/_/g, ' ').replace(/:(\d+)/, ' $1').toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase())
    .replace(/last n (\d+)/, 'Last $1')
    .replace(/next n (\d+)/, 'Next $1');

const OPERATOR_LABELS = {
  equals: 'is', not_equals: 'is not', contains: 'contains',
  does_not_contain: 'does not contain', starts_with: 'starts with',
  ends_with: 'ends with', is_empty: 'is empty', is_not_empty: 'is not empty',
  greater_than: 'is greater than', greater_than_equal: 'is at least',
  less_than: 'is less than', less_than_equal: 'is at most',
  between: 'is between', relative_range: 'is within', in: 'is any of',
  not_in: 'is none of',
};

const NO_VALUE = new Set(['is_empty', 'is_not_empty']);
const PREVIEW_DEBOUNCE_MS = 500;

export default function ReportBuilder() {
  const { reportId } = useParams();
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditing = Boolean(reportId);
  // A draft from "Create with AI" or the AI Assistant chat, already validated
  // by the backend. It seeds a new, unsaved report; Save is still the user's.
  const draft = !isEditing ? location.state?.draft ?? null : null;
  const draftDef = draft?.definition ?? {};

  const [objects, setObjects] = useState([]);
  const [fields, setFields] = useState([]);
  const [fieldSearch, setFieldSearch] = useState('');
  const [tab, setTab] = useState(0);

  const [name, setName] = useState(draft?.name || 'Untitled report');
  const [description, setDescription] = useState(draft?.description ?? '');
  const [primaryObject, setPrimaryObject] = useState(draft?.primary_object ?? '');
  const [columns, setColumns] = useState(() => normaliseColumns(draftDef.columns));
  const [conditions, setConditions] = useState(draftDef.filters?.conditions ?? []);
  const [logic, setLogic] = useState(draftDef.filters?.logic ?? 'AND');
  const [groupBy, setGroupBy] = useState(draftDef.group_by ?? []);
  const [aggregates, setAggregates] = useState(draftDef.aggregates ?? []);
  const [sort, setSort] = useState(draftDef.sort ?? []);
  const [scope, setScope] = useState(draftDef.scope ?? 'auto');

  // A second draft opened while the builder is already on /reports/new
  // (from the chat panel) does not remount the page, so apply it here.
  const appliedDraftKey = useRef(location.key);
  useEffect(() => {
    if (!draft || appliedDraftKey.current === location.key) return;
    appliedDraftKey.current = location.key;
    const def = draft.definition ?? {};
    setName(draft.name || 'Untitled report');
    setDescription(draft.description ?? '');
    setPrimaryObject(draft.primary_object ?? '');
    setColumns(normaliseColumns(def.columns));
    setConditions(def.filters?.conditions ?? []);
    setLogic(def.filters?.logic ?? 'AND');
    setGroupBy(def.group_by ?? []);
    setAggregates(def.aggregates ?? []);
    setSort(def.sort ?? []);
    setScope(def.scope ?? 'auto');
  }, [draft, location.key]);

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  // Set the instant the definition changes, not when the request starts.
  // The preview is debounced by half a second, so feedback tied to the
  // request leaves a click looking like it did nothing at all.
  const [previewPending, setPreviewPending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const fieldMap = useMemo(
    () => Object.fromEntries(fields.map((f) => [f.api_name, f])), [fields],
  );
  const isGrouped = groupBy.length > 0 || aggregates.length > 0;

  const definition = useMemo(() => {
    const base = { version: 1, primary_object: primaryObject, scope };
    if (isGrouped) {
      base.group_by = groupBy;
      base.aggregates = aggregates;
    } else {
      base.columns = columns;
    }
    if (conditions.length) base.filters = { logic, conditions };
    if (sort.length) base.sort = sort;
    return base;
  }, [primaryObject, scope, isGrouped, groupBy, aggregates, columns,
      conditions, logic, sort]);

  /* --------------------------------------------------------- loading */

  useEffect(() => {
    axiosInstance.get('/analytics/catalog/objects')
      .then(({ data }) => setObjects(Array.isArray(data) ? data : []))
      .catch(() => setToast({ severity: 'error',
        message: 'The list of reportable objects could not be loaded.' }))
      .finally(() => { if (!isEditing) setLoading(false); });
  }, [axiosInstance, isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    axiosInstance.get(`/analytics/reports/${reportId}`)
      .then(({ data }) => {
        const def = data.definition ?? {};
        setName(data.name);
        setDescription(data.description ?? '');
        setPrimaryObject(data.primary_object);
        setColumns(normaliseColumns(def.columns));
        setGroupBy(def.group_by ?? []);
        setAggregates(def.aggregates ?? []);
        setConditions(def.filters?.conditions ?? []);
        setLogic(def.filters?.logic ?? 'AND');
        setSort(def.sort ?? []);
        setScope(def.scope ?? 'auto');
      })
      .catch(() => setToast({ severity: 'error',
        message: 'That report could not be opened.' }))
      .finally(() => setLoading(false));
  }, [axiosInstance, reportId, isEditing]);

  useEffect(() => {
    if (!primaryObject) { setFields([]); return; }
    axiosInstance.get(`/analytics/catalog/objects/${primaryObject}/fields`)
      .then(({ data }) => setFields(Array.isArray(data) ? data : []))
      .catch(() => setFields([]));
  }, [axiosInstance, primaryObject]);

  /* ------------------------------------------------------- preview */

  const previewTimer = useRef(null);
  const previewSeq = useRef(0);

  const runPreview = useCallback(async () => {
    if (!primaryObject) {
      setPreview(null);
      setPreviewPending(false);
      return;
    }
    if (!isGrouped && !columns.length) {
      setPreview(null);
      setPreviewError(null);
      setPreviewPending(false);
      return;
    }
    const seq = ++previewSeq.current;
    setPreviewing(true);
    try {
      const { data } = await axiosInstance.post('/analytics/reports/run', {
        definition, limit: 100,
      });
      // A slower earlier request must not overwrite a newer result; without
      // this the preview flickers back to a stale shape while typing.
      if (seq !== previewSeq.current) return;
      setPreview(data);
      setPreviewError(null);
    } catch (err) {
      if (seq !== previewSeq.current) return;
      setPreview(null);
      setPreviewError(readError(err, 'This report could not be run.'));
    } finally {
      if (seq === previewSeq.current) {
        setPreviewing(false);
        setPreviewPending(false);
      }
    }
  }, [axiosInstance, definition, primaryObject, isGrouped, columns.length]);

  useEffect(() => {
    // runPreview is rebuilt whenever the definition changes, so this effect
    // firing IS the signal that something the user did needs re-running.
    setPreviewPending(true);
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(runPreview, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(previewTimer.current);
  }, [runPreview]);

  /* --------------------------------------------------------- actions */

  const changeObject = (apiName) => {
    // Everything selected refers to the old object's fields, so keeping any of
    // it would produce a definition the engine rejects on the next keystroke.
    setPrimaryObject(apiName);
    setColumns([]); setConditions([]); setGroupBy([]);
    setAggregates([]); setSort([]);
    setPreview(null); setPreviewError(null);
  };

  const addColumn = (field) => {
    if (columns.some((c) => c.field === field.api_name)) return;
    setColumns((prev) => [...prev, { field: field.api_name, label: field.label }]);
  };

  const addGrouping = (field) => {
    if (!field.groupable || groupBy.some((g) => g.field === field.api_name)) return;
    const entry = { field: field.api_name, label: field.label };
    if (field.date_buckets?.length) entry.bucket = 'month';
    setGroupBy((prev) => [...prev, entry]);
    // A grouping with nothing to measure lists distinct values; a count is
    // what people mean, and it is easy to remove.
    if (!aggregates.length) {
      setAggregates([{ field: 'id', fn: 'count', label: 'Record Count' }]);
    }
  };

  const save = async () => {
    if (!primaryObject) {
      setToast({ severity: 'warning', message: 'Choose an object to report on first.' });
      return;
    }
    setSaving(true);
    const payload = {
      name: name.trim() || 'Untitled report',
      description: description.trim() || null,
      primary_object: primaryObject,
      // matrix once there are two groupings and a measure: the viewer can
      // pivot that, and the kind is what decides which tab opens first.
      report_kind: !isGrouped
        ? 'tabular'
        : (groupBy.length >= 2 && aggregates.length ? 'matrix' : 'summary'),
      definition,
    };
    try {
      if (isEditing) {
        await axiosInstance.patch(`/analytics/reports/${reportId}`, payload);
        setToast({ severity: 'success', message: 'Saved.' });
      } else {
        const { data } = await axiosInstance.post('/analytics/reports', payload);
        navigate(`/reports/${data.id}`, { replace: true });
      }
    } catch (err) {
      setToast({ severity: 'error', message: readError(err, 'This report could not be saved.') });
    } finally {
      setSaving(false);
    }
  };

  // One flag for the whole "something is happening" state.
  const busy = previewing || previewPending;

  const filteredFields = useMemo(() => {
    const term = fieldSearch.trim().toLowerCase();
    if (!term) return fields;
    return fields.filter((f) =>
      `${f.label} ${f.api_name}`.toLowerCase().includes(term));
  }, [fields, fieldSearch]);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box display="flex" justifyContent="center" py={10}><CircularProgress /></Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* ---------------------------------------------------- header */}
        <Box display="flex" alignItems="center" gap={1.5} mb={2.5} flexWrap="wrap">
          <IconButton size="small" onClick={() => navigate('/reports')}>
            <ArrowBackRounded fontSize="small" />
          </IconButton>
          <TextField
            variant="standard" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Untitled report"
            sx={{ minWidth: 260,
                  '& .MuiInputBase-input': { fontSize: 22, fontWeight: 700 } }}
          />
          <Box flex={1} />
          {busy && <CircularProgress size={16} />}
          <MDButton variant="outlined" color="dark" onClick={() => navigate('/reports')}>
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={save}
                    disabled={saving || !primaryObject}>
            {saving ? 'Saving…' : isEditing ? 'Save' : 'Save report'}
          </MDButton>
        </Box>

        <Box display="flex" gap={2} alignItems="stretch"
             flexWrap={{ xs: 'wrap', lg: 'nowrap' }}>

          {/* ------------------------------------------------ left pane */}
          <Pane width={260}>
            <TextField
              select size="small" fullWidth label="Report on"
              value={primaryObject} onChange={(e) => changeObject(e.target.value)}
              sx={{ mb: 1.5 }}
            >
              {groupObjects(objects).map(([group, items]) => [
                <MenuItem key={group} disabled sx={{ opacity: 1 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                    {group.toUpperCase()}
                  </Typography>
                </MenuItem>,
                ...items.map((o) => (
                  <MenuItem key={o.api_name} value={o.api_name} sx={{ pl: 3 }}>
                    {o.plural_label}
                  </MenuItem>
                )),
              ])}
            </TextField>

            {primaryObject && (
              <>
                <TextField
                  size="small" fullWidth placeholder="Find a field…"
                  value={fieldSearch} onChange={(e) => setFieldSearch(e.target.value)}
                  sx={{ mb: 1 }}
                  InputProps={{ startAdornment: (
                    <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment>
                  ) }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
                  {isGrouped ? 'Click to group by' : 'Click to add as a column'}
                </Typography>
                <Box sx={{ mt: 0.5, maxHeight: 460, overflowY: 'auto' }}>
                  {filteredFields.map((field) => (
                    <FieldRow
                      key={field.api_name} field={field} grouped={isGrouped}
                      onClick={() => (isGrouped ? addGrouping(field) : addColumn(field))}
                    />
                  ))}
                  {!filteredFields.length && (
                    <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
                      No fields match.
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Pane>

          {/* ---------------------------------------------- centre pane */}
          <Box flex={1} minWidth={0}
               sx={{ border: '1px solid', borderColor: 'divider',
                     borderRadius: '14px', backgroundColor: colors.background.white,
                     overflow: 'hidden', position: 'relative' }}>
            {/* Indeterminate, at the top of the pane the user is watching.
                Shown while a run is pending as well as in flight, so adding a
                column reads as "working" immediately rather than after the
                debounce. */}
            {busy && (
              <LinearProgress
                sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2,
                      height: 3 }}
              />
            )}
            {previewError ? (
              <Alert severity="warning" sx={{ m: 2 }}>{previewError}</Alert>
            ) : !primaryObject ? (
              <Box sx={{ p: 8, textAlign: 'center' }}>
                <TuneRounded sx={{ fontSize: 40, opacity: 0.25, mb: 1 }} />
                <Typography variant="h6" gutterBottom>Choose what to report on</Typography>
                <Typography variant="body2" color="text.secondary">
                  Pick an object on the left, then add the fields you want to see.
                </Typography>
              </Box>
            ) : (
              <Box sx={{
                // Stale rows stay visible so the layout does not jump, but
                // dimmed: reading last keystroke's numbers as this one's is
                // the failure mode a spinner-only state invites.
                opacity: busy && preview ? 0.45 : 1,
                transition: 'opacity .15s',
                pointerEvents: busy ? 'none' : 'auto',
              }}>
                <ReportTable result={preview} maxHeight={560} dense />
              </Box>
            )}

            {/* First run has nothing to dim, so say so explicitly. */}
            {busy && !preview && !previewError && primaryObject && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'grid',
                         placeItems: 'center' }}>
                <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                  <CircularProgress size={26} />
                  <Typography variant="caption" color="text.secondary">
                    Running the report…
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* ----------------------------------------------- right pane */}
          <Pane width={330}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth"
                  sx={{ mb: 1.5 }}>
              <Tab label="Filters" sx={{ minWidth: 0 }} />
              <Tab label="Summarise" sx={{ minWidth: 0 }} />
              <Tab label="Options" sx={{ minWidth: 0 }} />
            </Tabs>

            {tab === 0 && (
              <FiltersTab
                fields={fields} fieldMap={fieldMap}
                conditions={conditions} setConditions={setConditions}
                logic={logic} setLogic={setLogic}
              />
            )}
            {tab === 1 && (
              <SummariseTab
                fields={fields} fieldMap={fieldMap}
                groupBy={groupBy} setGroupBy={setGroupBy}
                aggregates={aggregates} setAggregates={setAggregates}
                columns={columns} setColumns={setColumns}
              />
            )}
            {tab === 2 && (
              <OptionsTab
                description={description} setDescription={setDescription}
                scope={scope} setScope={setScope}
                sort={sort} setSort={setSort}
                preview={preview} isGrouped={isGrouped}
                fields={fields} columns={columns}
              />
            )}
          </Pane>
        </Box>
      </MDBox>

      <Snackbar
        open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

/* ----------------------------------------------------------------- tabs */

function FiltersTab({ fields, fieldMap, conditions, setConditions, logic, setLogic }) {
  const addCondition = (field) => {
    const nextId = conditions.reduce((max, c) => Math.max(max, c.id ?? 0), 0) + 1;
    setConditions((prev) => [...prev, {
      id: nextId, field: field.api_name,
      operator: field.operators?.[0] ?? 'equals', value: '',
    }]);
  };

  const update = (id, patch) =>
    setConditions((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));

  const remove = (id) => setConditions((prev) => prev.filter((c) => c.id !== id));

  return (
    <Box>
      <Autocomplete
        size="small" options={fields.filter((f) => f.filterable)} value={null}
        onChange={(_, field) => field && addCondition(field)}
        getOptionLabel={(o) => o?.label ?? ''}
        renderInput={(params) => <TextField {...params} label="Add a filter" />}
        sx={{ mb: 1.5 }}
      />

      {!conditions.length ? (
        <Typography variant="caption" color="text.secondary">
          No filters. The report will return everything you have access to.
        </Typography>
      ) : (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {conditions.map((condition) => {
            const field = fieldMap[condition.field];
            return (
              <Box key={condition.id}
                   sx={{ p: 1.25, borderRadius: '10px',
                         backgroundColor: colors.background.offWhite }}>
                <Box display="flex" alignItems="center" gap={0.5} mb={0.75}>
                  <Chip size="small" label={condition.id} sx={{ height: 18, fontSize: 10 }} />
                  <Typography variant="caption" fontWeight={700} flex={1} noWrap>
                    {field?.label ?? condition.field}
                  </Typography>
                  <IconButton size="small" onClick={() => remove(condition.id)}>
                    <ClearRounded sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>

                <TextField
                  select size="small" fullWidth value={condition.operator}
                  onChange={(e) => update(condition.id, { operator: e.target.value, value: '' })}
                  sx={{ mb: 0.75 }}
                >
                  {(field?.operators ?? ['equals']).map((op) => (
                    <MenuItem key={op} value={op}>{OPERATOR_LABELS[op] ?? op}</MenuItem>
                  ))}
                </TextField>

                <ConditionValue
                  condition={condition} field={field}
                  onChange={(value) => update(condition.id, { value })}
                />
              </Box>
            );
          })}

          {conditions.length > 1 && (
            <TextField
              size="small" fullWidth label="Combine with" value={logic}
              onChange={(e) => setLogic(e.target.value)}
              helperText="AND, OR, or an expression like 1 AND (2 OR 3)"
            />
          )}
        </Box>
      )}
    </Box>
  );
}

function ConditionValue({ condition, field, onChange }) {
  if (NO_VALUE.has(condition.operator)) return null;

  if (condition.operator === 'relative_range') {
    return (
      <TextField select size="small" fullWidth label="Period"
                 value={condition.value || 'THIS_MONTH'}
                 onChange={(e) => onChange(e.target.value)}>
        {RELATIVE_RANGES.map((token) => (
          <MenuItem key={token} value={token}>{rangeLabel(token)}</MenuItem>
        ))}
      </TextField>
    );
  }

  if (field?.picklist_options?.length) {
    const multi = condition.operator === 'in' || condition.operator === 'not_in';
    return (
      <Autocomplete
        size="small" multiple={multi} options={field.picklist_options}
        value={multi
          ? (Array.isArray(condition.value) ? condition.value : [])
          : (condition.value || null)}
        onChange={(_, value) => onChange(value)}
        renderInput={(params) => <TextField {...params} label="Value" />}
      />
    );
  }

  if (field?.type === 'date' || field?.type === 'datetime') {
    return (
      <TextField
        size="small" fullWidth type="date" label="Date" InputLabelProps={{ shrink: true }}
        value={condition.value || ''} onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  const numeric = field?.type === 'number' || field?.type === 'currency';
  return (
    <TextField
      size="small" fullWidth label="Value" type={numeric ? 'number' : 'text'}
      value={Array.isArray(condition.value) ? condition.value.join(', ') : (condition.value ?? '')}
      onChange={(e) => onChange(
        condition.operator === 'in' || condition.operator === 'not_in'
          ? e.target.value.split(',').map((v) => v.trim()).filter(Boolean)
          : e.target.value,
      )}
      helperText={condition.operator === 'in' || condition.operator === 'not_in'
        ? 'Separate values with commas' : undefined}
    />
  );
}

function SummariseTab({ fields, fieldMap, groupBy, setGroupBy, aggregates,
                        setAggregates, columns, setColumns }) {
  const groupable = fields.filter((f) => f.groupable);
  const summable = fields.filter((f) => f.aggregates?.length);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box>
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          GROUP BY
        </Typography>
        <Autocomplete
          size="small" options={groupable.filter((f) => !groupBy.some((g) => g.field === f.api_name))}
          value={null}
          onChange={(_, field) => field && setGroupBy((prev) => [...prev, {
            field: field.api_name, label: field.label,
            ...(field.date_buckets?.length ? { bucket: 'month' } : {}),
          }])}
          getOptionLabel={(o) => o?.label ?? ''}
          renderInput={(params) => <TextField {...params} label="Add a grouping" />}
          sx={{ my: 1 }}
          noOptionsText="No more fields can be grouped on"
        />
        {groupBy.map((entry, index) => {
          const field = fieldMap[entry.field];
          return (
            <Box key={entry.field} display="flex" alignItems="center" gap={0.5} mb={0.75}>
              <DragIndicatorRounded sx={{ fontSize: 16, color: colors.text.light }} />
              <Typography variant="body2" flex={1} noWrap>{field?.label ?? entry.field}</Typography>
              {field?.date_buckets?.length > 0 && (
                <TextField
                  select size="small" variant="standard" value={entry.bucket ?? 'month'}
                  onChange={(e) => setGroupBy((prev) => prev.map((g, i) =>
                    (i === index ? { ...g, bucket: e.target.value } : g)))}
                  sx={{ width: 84 }}
                >
                  {field.date_buckets.map((b) => (
                    <MenuItem key={b} value={b}>{b}</MenuItem>
                  ))}
                </TextField>
              )}
              <IconButton size="small"
                onClick={() => setGroupBy((prev) => prev.filter((_, i) => i !== index))}>
                <ClearRounded sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          );
        })}
      </Box>

      <Divider />

      <Box>
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          SUMMARISE
        </Typography>
        <Autocomplete
          size="small" options={summable} value={null}
          onChange={(_, field) => field && setAggregates((prev) => [...prev, {
            field: field.api_name,
            fn: field.aggregates.includes('sum') ? 'sum' : 'count',
            label: `${field.aggregates.includes('sum') ? 'Sum' : 'Count'} of ${field.label}`,
          }])}
          getOptionLabel={(o) => o?.label ?? ''}
          renderInput={(params) => <TextField {...params} label="Add a summary" />}
          sx={{ my: 1 }}
        />
        {aggregates.map((entry, index) => {
          const field = fieldMap[entry.field];
          return (
            <Box key={`${entry.fn}-${entry.field}`} display="flex" alignItems="center" gap={0.5} mb={0.75}>
              <FunctionsRounded sx={{ fontSize: 16, color: colors.text.light }} />
              <TextField
                select size="small" variant="standard" value={entry.fn}
                onChange={(e) => setAggregates((prev) => prev.map((a, i) =>
                  (i === index ? { ...a, fn: e.target.value } : a)))}
                sx={{ width: 110 }}
              >
                {(field?.aggregates ?? ['count']).map((fn) => (
                  <MenuItem key={fn} value={fn}>{fn.replace('_', ' ')}</MenuItem>
                ))}
              </TextField>
              <Typography variant="body2" flex={1} noWrap>{field?.label ?? entry.field}</Typography>
              <IconButton size="small"
                onClick={() => setAggregates((prev) => prev.filter((_, i) => i !== index))}>
                <ClearRounded sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          );
        })}
        {(groupBy.length > 0 || aggregates.length > 0) && (
          <Alert severity="info" sx={{ mt: 1, py: 0.25 }}>
            <Typography variant="caption">
              {groupBy.length >= 2 && aggregates.length
                ? 'Two groupings and a summary — this report can also be viewed '
                  + 'as a matrix, with the first grouping as rows and the second '
                  + 'as columns.'
                : 'This is a summary report. Columns are replaced by the '
                  + 'groupings and summaries above.'}
            </Typography>
          </Alert>
        )}
      </Box>

      {columns.length > 0 && groupBy.length === 0 && aggregates.length === 0 && (
        <>
          <Divider />
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary">
              COLUMNS
            </Typography>
            {columns.map((column, index) => (
              <Box key={column.field} display="flex" alignItems="center" gap={0.5} mt={0.75}>
                <DragIndicatorRounded sx={{ fontSize: 16, color: colors.text.light }} />
                <Typography variant="body2" flex={1} noWrap>
                  {fieldMap[column.field]?.label ?? column.field}
                </Typography>
                <IconButton size="small"
                  onClick={() => setColumns((prev) => prev.filter((_, i) => i !== index))}>
                  <ClearRounded sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

function OptionsTab({ description, setDescription, scope, setScope, sort, setSort,
                      preview, isGrouped, fields, columns }) {
  // A grouped report can only sort by what it selects, which is what the
  // engine enforces; offering anything else here would produce a 422 the user
  // cannot act on.
  const sortable = isGrouped
    ? (preview?.columns ?? []).map((c) => ({ value: c.key, label: c.label }))
    : columns.map((c) => ({
        value: c.field,
        label: fields.find((f) => f.api_name === c.field)?.label ?? c.field,
      }));

  const current = sort[0] ?? {};

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <TextField
        size="small" fullWidth multiline minRows={2} label="Description"
        value={description} onChange={(e) => setDescription(e.target.value)}
        helperText="What this report answers. Shown in the list."
      />

      <Box display="flex" gap={1}>
        <TextField
          select size="small" fullWidth label="Sort by"
          value={current.field ?? ''}
          onChange={(e) => setSort(e.target.value
            ? [{ field: e.target.value, direction: current.direction ?? 'desc' }]
            : [])}
        >
          <MenuItem value="">Default</MenuItem>
          {sortable.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          select size="small" label="Order" sx={{ width: 120 }}
          value={current.direction ?? 'desc'} disabled={!current.field}
          onChange={(e) => setSort([{ field: current.field, direction: e.target.value }])}
        >
          <MenuItem value="asc">Ascending</MenuItem>
          <MenuItem value="desc">Descending</MenuItem>
        </TextField>
      </Box>

      <TextField
        select size="small" fullWidth label="Records to include" value={scope}
        onChange={(e) => setScope(e.target.value)}
        helperText="This can only narrow what you already have access to."
      >
        <MenuItem value="auto">Everything I can see</MenuItem>
        <MenuItem value="mine">Only mine</MenuItem>
        <MenuItem value="team">My team&apos;s</MenuItem>
      </TextField>
    </Box>
  );
}

/* --------------------------------------------------------------- pieces */

function Pane({ width, children }) {
  return (
    <Box sx={{
      width: { xs: '100%', lg: width }, flexShrink: 0,
      border: '1px solid', borderColor: 'divider', borderRadius: '14px',
      p: 1.5, backgroundColor: colors.background.white,
    }}>
      {children}
    </Box>
  );
}

function FieldRow({ field, grouped, onClick }) {
  const disabled = grouped && !field.groupable;
  return (
    <Tooltip
      title={disabled
        ? 'This is free text — grouping on it would make one group per record'
        : ''}
      placement="right"
    >
      <Box
        onClick={disabled ? undefined : onClick}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 1, py: 0.75, borderRadius: '8px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.4 : 1,
          '&:hover': { backgroundColor: disabled ? 'transparent' : colors.background.offWhite },
        }}
      >
        <AddRounded sx={{ fontSize: 14, color: colors.text.light }} />
        <Typography variant="body2" flex={1} noWrap>{field.label}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
          {field.type}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function groupObjects(objects) {
  const groups = new Map();
  objects.forEach((o) => {
    if (!groups.has(o.group_label)) groups.set(o.group_label, []);
    groups.get(o.group_label).push(o);
  });
  return [...groups.entries()];
}

function normaliseColumns(columns) {
  if (!Array.isArray(columns)) return [];
  return columns.map((c) => (typeof c === 'string' ? { field: c } : c));
}

function readError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
}
